import { ArrowUpRight, Lock } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { track } from "@/lib/analytics";
import { cn } from "@/lib/utils";

import sfnMark from "@/assets/ecosystem/sfn-mark.png.asset.json";
import plantyMark from "@/assets/ecosystem/plantypando.jpeg.asset.json";
import aquaMark from "@/assets/ecosystem/aquapando.jpeg.asset.json";
import solarMark from "@/assets/ecosystem/solarpando.png.asset.json";
import villageMark from "@/assets/ecosystem/villagepando.jpeg.asset.json";

type PortalStatus = "live" | "next" | "phase2";

type Portal = {
  id: string;
  title: string;
  subtitle: string;
  img: string;
  href: string;
  cta: string;
  status: PortalStatus;
};

const PORTALS: Portal[] = [
  {
    id: "sfn",
    title: "SFN Hub",
    subtitle: "Culinary & Food Security Network",
    img: sfnMark.url,
    href: "/",
    cta: "Launch SFN App",
    status: "live",
  },
  {
    id: "solar",
    title: "SolarPando",
    subtitle: "Green Energy & Solar Infrastructure",
    img: solarMark.url,
    href: "https://solarpando.io",
    cta: "Launch Next",
    status: "next",
  },
  {
    id: "planty",
    title: "PlantyPando",
    subtitle: "Agriculture & Land Reserves",
    img: plantyMark.url,
    href: "https://plantypando.io",
    cta: "Coming Soon",
    status: "phase2",
  },
  {
    id: "aqua",
    title: "AquaPando",
    subtitle: "Water Management & Aquaponics",
    img: aquaMark.url,
    href: "https://aquapando.io",
    cta: "Coming Soon",
    status: "phase2",
  },
  {
    id: "village",
    title: "VillagePando",
    subtitle: "Autonomous Living & Micro-Apartments",
    img: villageMark.url,
    href: "https://villagepando.io",
    cta: "Coming Soon",
    status: "phase2",
  },
];

const BADGE_LABEL: Record<PortalStatus, string> = {
  live: "Live",
  next: "Launch Next",
  phase2: "Phase 2",
};

export function EcosystemPortals({ className }: { className?: string }) {
  return (
    <section
      aria-labelledby="ecosystem-portals-title"
      className={cn("relative", className)}
    >
      <header className="mb-8 flex flex-col items-start gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-300/80">
          Direct Access Portals
        </span>
        <h2
          id="ecosystem-portals-title"
          className="font-display text-3xl font-medium tracking-tight text-white sm:text-4xl"
        >
          Explore the Ecosystem Infrastructure
        </h2>
        <p className="max-w-2xl text-sm text-white/60">
          Five sovereign nodes. One circular economy. Each portal leads directly to
          its dedicated application layer.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {PORTALS.map((p) => (
          <PortalCard key={p.id} portal={p} />
        ))}
      </div>
    </section>
  );
}

function StatusBadge({ status }: { status: PortalStatus }) {
  return (
    <span
      className={cn(
        "absolute right-3 top-3 z-10 rounded-full border px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.18em]",
        status === "live" && "border-emerald-400/50 bg-emerald-400/10 text-emerald-300",
        status === "next" &&
          "border-amber-300/50 bg-amber-300/10 text-amber-200 shadow-[0_0_18px_-6px_#FBBF24]",
        status === "phase2" && "border-white/15 bg-white/[0.04] text-white/50",
      )}
    >
      {BADGE_LABEL[status]}
    </span>
  );
}

function PortalCard({ portal }: { portal: Portal }) {
  const locked = portal.status !== "live";
  const onClick = () =>
    track("cta_click", { id: `portal_${portal.id}`, surface: "ecosystem_portals" });

  const Inner = (
    <>
      <StatusBadge status={portal.status} />
      <div className="relative mb-5 grid place-items-center">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-60"
          style={{ background: "radial-gradient(50% 50% at 50% 50%, #10B98155, transparent 70%)" }}
        />
        <img
          src={portal.img}
          alt=""
          className={cn(
            "relative h-24 w-24 rounded-full border border-white/10 object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05]",
            locked && "opacity-60 saturate-50",
          )}
          loading="lazy"
          decoding="async"
        />
      </div>
      <h3 className="text-center font-display text-lg font-semibold text-white">
        {portal.title}
      </h3>
      <p className="mt-1 min-h-[2.5rem] text-center text-xs text-white/60">
        {portal.subtitle}
      </p>
      <span
        className={cn(
          "mt-5 inline-flex w-full items-center justify-center gap-1.5 rounded-full border px-4 py-2.5",
          "font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-300",
          portal.status === "live" &&
            "border-emerald-400/40 bg-transparent text-emerald-300 group-hover:bg-emerald-400 group-hover:text-black",
          portal.status === "next" &&
            "border-amber-300/40 bg-amber-300/[0.06] text-amber-200",
          portal.status === "phase2" &&
            "border-white/10 bg-white/[0.02] text-white/40",
        )}
      >
        {portal.cta}
        {portal.status === "live" ? (
          <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.5} />
        ) : (
          <Lock className="h-3 w-3" strokeWidth={2.5} />
        )}
      </span>
    </>
  );

  const cls = cn(
    "group relative flex flex-col rounded-2xl border p-6 transition-all duration-300",
    "border-[#1E293B] bg-[#0F172A]",
    portal.status === "live" &&
      "hover:-translate-y-1 hover:border-emerald-400/70 hover:shadow-[0_20px_60px_-20px_#10B98180]",
    portal.status === "next" &&
      "border-amber-300/25 hover:-translate-y-0.5 hover:border-amber-300/50 hover:shadow-[0_20px_60px_-20px_#FBBF2480]",
    portal.status === "phase2" && "opacity-80",
  );

  if (locked) {
    return (
      <div
        className={cls}
        aria-label={`${portal.title} — ${BADGE_LABEL[portal.status]}`}
        aria-disabled="true"
      >
        {Inner}
      </div>
    );
  }

  return (
    <Link to={portal.href} onClick={onClick} className={cls} aria-label={portal.cta}>
      {Inner}
    </Link>
  );
}
